import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  BarChart3,
  Crown,
  Users,
  FileUp,
  Package,
  Megaphone,
  Settings,
  LogOut,
  X,
  type LucideIcon,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { avatarInitials } from "@/shared/lib/avatar";

interface NavEntry {
  key: string;
  labelKey: string;
  icon: LucideIcon;
  to?: string; // present = live route; absent = disabled placeholder
  count?: number;
}

interface NavGroup {
  key: string;
  labelKey: string;
  items: NavEntry[];
}

interface SidebarProps {
  customerCount?: number;
  // "dock" = the in-grid column (full at >1100px, icon rail at 768–1100px, hidden below 768px).
  // "sheet" = the same nav inside the mobile off-canvas panel (always full labels).
  variant?: "dock" | "sheet";
  // Sheet only: close control + called after a nav tap (tapping the active route
  // doesn't change the location, so the route-change effect alone can't close it).
  onClose?: () => void;
}

export function Sidebar({ customerCount, variant = "dock", onClose }: SidebarProps) {
  const isSheet = variant === "sheet";
  const { t } = useTranslation();
  const operator = useAuthStore((s) => s.operator);
  const logout = useAuthStore((s) => s.logout);

  // Grouped by what the operator is doing: look at the numbers, work with people,
  // run a tool, configure the system. Each entry also shows a one-line `nav.hint.<key>`.
  const groups: NavGroup[] = [
    {
      key: "main",
      labelKey: "nav.sectionMain",
      items: [
        { key: "overview", labelKey: "nav.overview", icon: LayoutDashboard, to: "/overview" },
        { key: "metrics", labelKey: "nav.metrics", icon: BarChart3, to: "/metrics" },
      ],
    },
    {
      key: "clients",
      labelKey: "nav.sectionClients",
      items: [
        { key: "renewals", labelKey: "nav.renewals", icon: Crown, to: "/renewals" },
        { key: "customers", labelKey: "nav.customers", icon: Users, to: "/customers", count: customerCount },
      ],
    },
    {
      key: "tools",
      labelKey: "nav.sectionTools",
      items: [
        { key: "import", labelKey: "nav.import", icon: FileUp, to: "/import" },
        { key: "catalogue", labelKey: "nav.catalogue", icon: Package, to: "/catalogue" },
        { key: "notifications", labelKey: "nav.notifications", icon: Megaphone, to: "/notifications" },
      ],
    },
    {
      key: "system",
      labelKey: "nav.sectionSystem",
      items: [{ key: "settings", labelKey: "nav.settings", icon: Settings, to: "/settings" }],
    },
  ];

  return (
    <aside
      className={`flex min-h-0 flex-col bg-white ${isSheet ? "h-full" : "sb-dock border-r border-grey-200"}`}
    >
      {/* Brand + env pill (+ close on the mobile sheet) */}
      <div className={`flex items-center gap-2.5 ${isSheet ? "px-5 pb-3 pt-4" : "px-[22px] pb-[18px] pt-[22px]"}`}>
        <img src="/assets/nasiyah-wordmark.svg" alt="Nasiyah" className="h-[26px] w-auto sb-wm" />
        <span className="sb-env ml-auto rounded-md bg-grey-100 px-[7px] py-[3px] text-[10.5px] font-bold uppercase tracking-wider text-grey-500">
          {t("app.support")}
        </span>
        {isSheet && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t("topbar.closeMenu")}
            className="-mr-2 flex h-10 w-10 flex-none items-center justify-center rounded-control text-grey-500 transition-transform hover:bg-grey-50 hover:text-grey-800 active:scale-[0.96]"
          >
            <X size={20} strokeWidth={1.8} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex flex-1 flex-col overflow-y-auto px-3.5 pb-3">
        {groups.map((g) => (
          <div key={g.key} role="group" aria-label={t(g.labelKey)} className="flex flex-col gap-0.5">
            <SectionLabel>{t(g.labelKey)}</SectionLabel>
            {g.items.map((e) => (
              <NavRow key={e.key} entry={e} t={t} onNavigate={onClose} />
            ))}
          </div>
        ))}
      </nav>

      {/* Operator profile + logout */}
      <div
        className={`flex items-center gap-2.5 border-t border-grey-200 px-3.5 ${
          isSheet ? "pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3" : "py-3"
        }`}
      >
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-pill bg-green text-[14px] font-bold text-white">
          {avatarInitials(operator?.name ?? "?")}
        </div>
        <div className="sb-who min-w-0">
          <b className="block overflow-hidden text-ellipsis whitespace-nowrap text-[13.5px] font-semibold text-grey-800">
            {operator?.name ?? "—"}
          </b>
          <span className="text-[12px] text-grey-500">{operator?.phone ?? ""}</span>
        </div>
        <button
          type="button"
          onClick={logout}
          aria-label={t("nav.logout")}
          title={t("nav.logout")}
          className="sb-logout ml-auto rounded-lg p-2 text-grey-400 md:p-1.5 hover:bg-grey-50 hover:text-grey-600"
        >
          <LogOut size={18} strokeWidth={1.8} />
        </button>
      </div>
    </aside>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="sb-section px-[11px] pb-1.5 pt-4 text-[11px] font-bold uppercase tracking-[0.06em] text-grey-400">
      {children}
    </div>
  );
}

function NavRow({
  entry,
  t,
  onNavigate,
}: {
  entry: NavEntry;
  t: (k: string) => string;
  onNavigate?: () => void;
}) {
  const Icon = entry.icon;
  const label = t(entry.labelKey);
  const hint = t(`nav.hint.${entry.key}`);

  const inner = (active: boolean) => (
    <>
      {/* Active marker on the sidebar edge — survives the collapsed icon rail. */}
      {active && (
        <span aria-hidden className="absolute bottom-2 left-[-14px] top-2 w-[3px] rounded-r-pill bg-green" />
      )}
      <Icon size={19} strokeWidth={1.8} className={`flex-none ${active ? "text-green" : "text-grey-400 group-hover:text-grey-600"}`} />
      <span className="sb-label min-w-0 flex-1">
        <span className={`block truncate text-[14px] leading-5 ${active ? "font-semibold" : "font-medium"}`}>
          {label}
        </span>
        <span className={`block truncate text-[11.5px] leading-4 ${active ? "text-green-dark/70" : "text-grey-400"}`}>
          {hint}
        </span>
      </span>
      {typeof entry.count === "number" && entry.count > 0 && (
        <span className="sb-count ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-pill bg-red-bright px-[5px] text-[11.5px] font-bold text-white">
          {entry.count}
        </span>
      )}
    </>
  );

  if (!entry.to) {
    // Disabled placeholder: visible but inert.
    return (
      <div
        aria-disabled
        title={t("placeholder.soon")}
        className="sb-nav-item relative flex cursor-not-allowed items-center gap-[11px] rounded-[9px] px-[11px] py-2 text-grey-400 opacity-70"
      >
        {inner(false)}
      </div>
    );
  }

  return (
    <NavLink
      to={entry.to}
      title={`${label} — ${hint}`}
      onClick={onNavigate}
      className={({ isActive }) =>
        `sb-nav-item group relative flex items-center gap-[11px] rounded-[9px] px-[11px] py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green/40 ${
          isActive ? "bg-green-bg text-green-dark" : "text-grey-700 hover:bg-grey-50 hover:text-grey-900 active:bg-grey-100"
        }`
      }
    >
      {({ isActive }) => inner(isActive)}
    </NavLink>
  );
}
